import { forwardRef, type RefObject, useImperativeHandle, useRef } from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { prizesImage } from '@/common/config/prizes-image';

import { MotionPicture } from '../Picture/Picture';

import purpleWheelPng from '/img/contract/purplewheel.png';
import purpleWheelWebp from '/img/contract/purplewheel.webp';
import lightPng from '/img/contract/vertical-light.png';
import lightWebp from '/img/contract/vertical-light.webp';

interface FortuneWheelProps {
  className?: string;
}

interface FortuneWheelChildrenRefs {
  light: RefObject<HTMLElement>;
  blur: RefObject<HTMLElement>;
  stars: RefObject<HTMLElement>;
  box: RefObject<HTMLElement>;
}

const FortuneWheel = forwardRef<FortuneWheelChildrenRefs, FortuneWheelProps>(
  ({ className }, ref) => {
    const eliteBoxStarsRef = useRef<HTMLElement>(null);
    const eliteLightRef = useRef<HTMLElement>(null);
    const eliteBlurRef = useRef<HTMLDivElement>(null);
    const eliteBoxRef = useRef<HTMLElement>(null);

    useImperativeHandle(ref, () => ({
      light: eliteLightRef,
      blur: eliteBlurRef,
      stars: eliteBoxStarsRef,
      box: eliteBoxRef,
    }));

    return (
      <div className={cn('relative flex', className)}>
        <MotionPicture
          initial={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
          ref={eliteLightRef}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 aspect-square w-[190px]"
          img={lightPng}
          webp={lightWebp}
          alt="light"
        />
        <motion.div
          ref={eliteBlurRef}
          initial={{ width: 90, left: 16, bottom: -5 }}
          className="absolute aspect-[90/155] w-[90px] left-[16px] -bottom-[5px] flex items-center justify-center"
        >
          <span className="absolute w-full h-full rounded-[50%] bg-irrigo-purple blur-[56px]"></span>
          <span className="absolute aspect-[69/118] w-[76.66%] rounded-[50%] bg-irrigo-purple blur-[18px]"></span>
        </motion.div>
        <MotionPicture
          ref={eliteBoxStarsRef}
          img={prizesImage.png}
          webp={prizesImage.webp}
          initial={{ width: 164, opacity: 1 }}
          className="absolute aspect-square -top-[43px] -right-[54px] w-[164px]"
          alt="stars"
        />
        <MotionPicture
          ref={eliteBoxRef}
          initial={{
            width: 128,
            height: 128,
            filter: 'drop-shadow(16px -16px 24px rgba(24, 26, 32, 0.50))',
          }}
          img={purpleWheelPng}
          webp={purpleWheelWebp}
          alt="elite box"
        />
      </div>
    );
  },
);

export { FortuneWheel };
